// Chart kinds beyond bar/line/area/donut: flows, hierarchies, matrices, gauges and networks.
// Same contract as charts.ts: options are rebuilt field by field from the validated spec,
// every kind lists the ECharts modules it needs so the runtime loads only those, and every
// kind has a table fallback and a text summary for screen readers. Compilers also tolerate
// data that slipped past validation (stale PHP-side documents): bad links are dropped, not thrown.
import * as s from './schema-dsl.ts'
import { INTENTS } from './tokens.ts'
import { toBcp47 } from './format.ts'
import {
  LABEL, commonChartProps, registerChartKind, baseOption, legendOption, tooltipOption, valueAxis, categoryAxis,
  colorFor, withAlpha, fmt, valueFormatOf, describeExtremes,
  type ChartContext, type CompiledChart, type EChartsModule, type TableFallback
} from './charts.ts'

const intent = s.enumOf(INTENTS)
const nullableNumber = s.anyOf([s.number(), s.nullValue()])
const NODE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const title = (spec: any) => spec.title ? spec.title + '. ' : ''
const sum = (values: (number | null | undefined)[]) => values.reduce<number>((total, v) => total + (typeof v === 'number' ? v : 0), 0)
const reducedMotion = (ctx: ChartContext) => ctx.animation.animation === false

function visualRange (values: (number | null)[]): [number, number] {
  const nums = values.filter((v): v is number => typeof v === 'number')
  if (!nums.length) return [0, 1]
  const lo = Math.min(...nums)
  const hi = Math.max(...nums)
  return lo === hi ? [Math.min(0, lo), hi || 1] : [lo, hi]
}

function visualMap (ctx: ChartContext, range: [number, number]): Record<string, unknown> {
  return {
    type: 'continuous',
    min: range[0],
    max: range[1],
    calculable: false,
    orient: 'horizontal',
    left: 'center',
    bottom: 0,
    itemHeight: 120,
    itemWidth: 10,
    textStyle: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize },
    inRange: { color: [withAlpha(ctx.theme.palette[0]!, 0.08), withAlpha(ctx.theme.palette[0]!, 0.55), ctx.theme.palette[0]!] }
  }
}

// ---------------------------------------------------------------------------
// Funnel

const funnelSchema = s.object({
  kind: s.literal('funnel'),
  items: s.array(s.object({ label: LABEL, value: s.number({ min: 0 }), intent }, { required: ['label', 'value'] }), { min: 2, max: 12 }),
  sort: s.enumOf(['descending', 'none'], 'descending (default) orders stages by size; none keeps the given order'),
  ...commonChartProps
}, { required: ['kind', 'items'] })

function compileFunnel (spec: any, ctx: ChartContext): CompiledChart {
  const items: { label: string, value: number, intent?: any }[] = spec.items
  const first = items[0]?.value ?? 0
  const option = {
    ...baseOption(ctx),
    color: items.map((item, i) => colorFor(ctx, i, item.intent)),
    tooltip: tooltipOption(ctx, 'item'),
    series: [{
      type: 'funnel',
      name: spec.title ?? ctx.labels.value,
      left: '8%',
      width: '84%',
      top: 8,
      bottom: 8,
      minSize: '12%',
      gap: 3,
      sort: spec.sort === 'none' ? 'none' : 'descending',
      label: { show: true, position: 'inside', color: '#ffffff', fontSize: ctx.theme.fontSize + 1, formatter: '{b}' },
      itemStyle: { borderColor: ctx.theme.surface, borderWidth: 1 },
      emphasis: { label: { fontWeight: 700 } },
      data: items.map((item, i) => ({ name: item.label, value: item.value, itemStyle: { color: colorFor(ctx, i, item.intent) } }))
    }]
  }
  const last = items[items.length - 1]!
  return {
    option,
    modules: ['FunnelChart', 'TooltipComponent', 'AriaComponent'],
    renderer: 'svg',
    table: {
      columns: [
        { key: 'label', label: ctx.labels.category },
        { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' },
        { key: 'conversion', label: ctx.labels.conversion, format: 'percent', align: 'end' }
      ],
      rows: items.map(item => ({ label: item.label, value: item.value, conversion: first ? item.value / first : null }))
    },
    summary: `${title(spec)}Funnel with ${items.length} stages from ${first ? fmt(spec, ctx, first) : 0} (${items[0]?.label}) to ${fmt(spec, ctx, last.value)} (${last.label})` +
      (first ? `, ${Math.round(last.value / first * 100)} % of the first stage.` : '.'),
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Gauge

const gaugeSchema = s.object({
  kind: s.literal('gauge'),
  value: s.number(),
  min: s.number(),
  max: s.number(),
  label: s.string({ max: 60 }),
  bands: s.array(s.object({ to: s.number(), intent }, { required: ['to', 'intent'] }), { max: 5, description: 'Coloured ranges from min up to each "to", in ascending order' }),
  ...commonChartProps
}, {
  required: ['kind', 'value', 'max'],
  refine (v, _p, report) {
    const min = typeof v.min === 'number' ? v.min : 0
    if ((v.max as number) <= min) report('max', 'must be greater than min')
    const bands = (v.bands as { to: number }[] | undefined) ?? []
    bands.forEach((band, i) => {
      if (i > 0 && band.to <= bands[i - 1]!.to) report(`bands[${i}].to`, 'bands must be in ascending order')
    })
  }
})

function compileGauge (spec: any, ctx: ChartContext): CompiledChart {
  const min: number = typeof spec.min === 'number' ? spec.min : 0
  const max: number = spec.max > min ? spec.max : min + 1
  const value = Math.min(Math.max(spec.value, min), max)
  const bands: { to: number, intent: any }[] = spec.bands ?? []
  const stops: [number, string][] = bands
    .filter(b => b.to > min)
    .map(b => [Math.min(1, (b.to - min) / (max - min)), colorFor(ctx, 0, b.intent)])
  if (stops.length && stops[stops.length - 1]![0]! < 1) stops.push([1, withAlpha(ctx.theme.axisLine, 0.35)])
  const bandColor = stops.find(([to]) => (value - min) / (max - min) <= to)?.[1]
  const option = {
    ...baseOption(ctx),
    series: [{
      type: 'gauge',
      min,
      max,
      startAngle: 210,
      endAngle: -30,
      radius: '92%',
      center: ['50%', '58%'],
      progress: stops.length ? { show: false } : { show: true, width: 14, roundCap: true, itemStyle: { color: ctx.theme.palette[0] } },
      axisLine: { roundCap: !stops.length, lineStyle: { width: 14, color: stops.length ? stops : [[1, withAlpha(ctx.theme.axisLine, 0.35)]] } },
      pointer: stops.length ? { show: true, length: '58%', width: 5, itemStyle: { color: bandColor ?? ctx.theme.text } } : { show: false },
      anchor: { show: !!stops.length, size: 10, itemStyle: { color: ctx.theme.text } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      title: { show: !!spec.label, offsetCenter: [0, '42%'], color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize + 1 },
      detail: { valueAnimation: !reducedMotion(ctx), offsetCenter: [0, stops.length ? '72%' : '8%'], color: ctx.theme.text, fontSize: 26, fontWeight: 700, fontFamily: ctx.theme.fontFamily },
      data: [{ value, name: spec.label ?? '' }]
    }]
  }
  return {
    option,
    modules: ['GaugeChart', 'AriaComponent'],
    renderer: 'svg',
    table: {
      columns: [{ key: 'label', label: ctx.labels.category }, { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' }, { key: 'max', label: ctx.labels.high, format: spec.format, align: 'end' }],
      rows: [{ label: spec.label ?? '', value: spec.value, max: spec.max }]
    },
    summary: `${title(spec)}Gauge at ${fmt(spec, ctx, spec.value)} on a scale from ${fmt(spec, ctx, min)} to ${fmt(spec, ctx, max)}.`,
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Treemap and sunburst: two levels, items with a value or children

const leafSchema = s.object({ label: LABEL, value: s.number({ min: 0 }), intent }, { required: ['label', 'value'] })
const hierarchyItem = s.object({
  label: LABEL,
  value: s.number({ min: 0 }),
  intent,
  children: s.array(leafSchema, { min: 1, max: 50 })
}, {
  required: ['label'],
  refine (v, _p, report) {
    if (v.value === undefined && v.children === undefined) report('', 'needs value or children')
  }
})

function hierarchySchema (kind: string) {
  return s.object({
    kind: s.literal(kind),
    items: s.array(hierarchyItem, { min: 1, max: 50 }),
    ...commonChartProps
  }, {
    required: ['kind', 'items'],
    refine (v, _p, report) {
      const leaves = (v.items as { children?: unknown[] }[]).reduce((n, i) => n + (i.children?.length ?? 1), 0)
      if (leaves > 500) report('items', `has ${leaves} leaves; the limit is 500`)
    }
  })
}

interface HierarchyItem { label: string, value?: number, intent?: any, children?: { label: string, value: number, intent?: any }[] }

function hierarchyData (items: HierarchyItem[], ctx: ChartContext) {
  return items.map((item, i) => {
    const color = colorFor(ctx, i, item.intent)
    const children = item.children?.map((c, ci) => ({
      name: c.label,
      value: c.value,
      itemStyle: { color: c.intent && c.intent !== 'neutral' ? colorFor(ctx, 0, c.intent) : withAlpha(color, 1 - Math.min(0.55, ci * 0.08)) }
    }))
    return { name: item.label, value: item.value ?? sum(children?.map(c => c.value) ?? []), itemStyle: { color }, ...(children ? { children } : {}) }
  })
}

function hierarchyTable (items: HierarchyItem[], ctx: ChartContext, spec: any): TableFallback {
  const rows: Record<string, string | number | null>[] = []
  const total = sum(items.map(i => i.value ?? sum(i.children?.map(c => c.value) ?? [])))
  for (const item of items) {
    if (item.children) {
      for (const c of item.children) rows.push({ label: `${item.label} › ${c.label}`, value: c.value, share: total ? c.value / total : null })
    } else {
      rows.push({ label: item.label, value: item.value ?? null, share: total && item.value ? item.value / total : null })
    }
  }
  return {
    columns: [
      { key: 'label', label: ctx.labels.category },
      { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' },
      { key: 'share', label: ctx.labels.share, format: 'percent', align: 'end' }
    ],
    rows
  }
}

function hierarchySummary (noun: string, items: HierarchyItem[], ctx: ChartContext, spec: any): string {
  const data = items.map(i => ({ label: i.label, value: i.value ?? sum(i.children?.map(c => c.value) ?? []) }))
  const total = sum(data.map(d => d.value))
  const top = [...data].sort((a, b) => b.value - a.value)[0]
  return `${title(spec)}${noun} of ${data.length} groups totalling ${fmt(spec, ctx, total)}.` +
    (top && total ? ` Largest: ${top.label} (${Math.round(top.value / total * 100)} %).` : '')
}

function compileTreemap (spec: any, ctx: ChartContext): CompiledChart {
  const items: HierarchyItem[] = spec.items
  const nested = items.some(i => i.children)
  const option = {
    ...baseOption(ctx),
    tooltip: tooltipOption(ctx, 'item'),
    series: [{
      type: 'treemap',
      name: spec.title ?? ctx.labels.value,
      roam: false,
      nodeClick: false,
      breadcrumb: { show: false },
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      label: { show: true, color: '#ffffff', fontSize: ctx.theme.fontSize + 1, overflow: 'truncate', formatter: '{b}' },
      upperLabel: nested ? { show: true, height: 22, color: '#ffffff', fontWeight: 600, fontSize: ctx.theme.fontSize } : undefined,
      itemStyle: { borderColor: ctx.theme.surface, borderWidth: 2, gapWidth: 2, borderRadius: 4 },
      levels: nested ? [{ itemStyle: { gapWidth: 3, borderWidth: 0 } }, { itemStyle: { gapWidth: 1, borderWidth: 0 } }] : undefined,
      data: hierarchyData(items, ctx)
    }]
  }
  return {
    option,
    modules: ['TreemapChart', 'TooltipComponent', 'AriaComponent'],
    renderer: 'svg',
    table: hierarchyTable(items, ctx, spec),
    summary: hierarchySummary('Treemap', items, ctx, spec),
    valueFormat: valueFormatOf(spec)
  }
}

function compileSunburst (spec: any, ctx: ChartContext): CompiledChart {
  const items: HierarchyItem[] = spec.items
  const option = {
    ...baseOption(ctx),
    tooltip: tooltipOption(ctx, 'item'),
    series: [{
      type: 'sunburst',
      name: spec.title ?? ctx.labels.value,
      radius: [0, '92%'],
      nodeClick: false,
      itemStyle: { borderColor: ctx.theme.surface, borderWidth: 2, borderRadius: 4 },
      label: { color: '#ffffff', fontSize: ctx.theme.fontSize, minAngle: 12, rotate: 'radial', formatter: '{b}' },
      emphasis: { focus: 'ancestor' },
      levels: [{}, { r0: '18%', r: '62%' }, { r0: '62%', r: '92%', label: { align: 'right' } }],
      data: hierarchyData(items, ctx)
    }]
  }
  return {
    option,
    modules: ['SunburstChart', 'TooltipComponent', 'AriaComponent'],
    renderer: 'svg',
    table: hierarchyTable(items, ctx, spec),
    summary: hierarchySummary('Sunburst chart', items, ctx, spec),
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Heatmap (categories x categories) and calendar (one value per day)

const heatmapSchema = s.object({
  kind: s.literal('heatmap'),
  xCategories: s.array(s.string({ max: 40 }), { min: 1, max: 60 }),
  yCategories: s.array(s.string({ max: 40 }), { min: 1, max: 40 }),
  values: s.array(s.array(nullableNumber, { max: 60 }), { min: 1, max: 40, description: 'One row per y category, one value per x category' }),
  xLabel: s.string({ max: 60 }),
  yLabel: s.string({ max: 60 }),
  ...commonChartProps
}, {
  required: ['kind', 'xCategories', 'yCategories', 'values'],
  refine (v, _p, report) {
    const rows = v.values as unknown[][]
    const x = (v.xCategories as unknown[]).length
    if (rows.length !== (v.yCategories as unknown[]).length) report('values', `must have one row per y category (${(v.yCategories as unknown[]).length})`)
    rows.forEach((row, i) => { if (row.length !== x) report(`values[${i}]`, `must have one value per x category (${x})`) })
  }
})

function compileHeatmap (spec: any, ctx: ChartContext): CompiledChart {
  const xs: string[] = spec.xCategories
  const ys: string[] = spec.yCategories
  const data: { name: string, value: [number, number, number | null] }[] = []
  ys.forEach((y, yi) => xs.forEach((x, xi) => {
    const v = spec.values[yi]?.[xi]
    data.push({ name: `${y} · ${x}`, value: [xi, yi, typeof v === 'number' ? v : null] })
  }))
  const range = visualRange(data.map(d => d.value[2]))
  const option = {
    ...baseOption(ctx),
    tooltip: tooltipOption(ctx, 'item'),
    grid: { left: 8, right: 8, top: 8, bottom: 40, containLabel: true },
    xAxis: { ...categoryAxis(ctx, xs, spec.xLabel), splitArea: { show: false } },
    yAxis: { ...categoryAxis(ctx, ys, spec.yLabel), nameLocation: 'end', inverse: true },
    visualMap: visualMap(ctx, range),
    series: [{
      type: 'heatmap',
      name: spec.title ?? ctx.labels.value,
      data,
      label: { show: data.length <= 120, color: ctx.theme.text, fontSize: ctx.theme.fontSize - 1 },
      itemStyle: { borderColor: ctx.theme.surface, borderWidth: 2, borderRadius: 3 },
      emphasis: { itemStyle: { borderColor: ctx.theme.text, borderWidth: 1 } }
    }]
  }
  const peak = data.reduce<typeof data[number] | null>((best, d) => (typeof d.value[2] === 'number' && (!best || d.value[2] > (best.value[2] as number)) ? d : best), null)
  return {
    option,
    modules: ['HeatmapChart', 'GridComponent', 'VisualMapComponent', 'TooltipComponent', 'AriaComponent'],
    renderer: data.length > 1500 ? 'canvas' : 'svg',
    table: {
      columns: [{ key: 'y', label: spec.yLabel ?? ctx.labels.category }, ...xs.map((x, i) => ({ key: 'x' + i, label: x, format: spec.format, align: 'end' as const }))],
      rows: ys.map((y, yi) => {
        const row: Record<string, string | number | null> = { y }
        xs.forEach((_x, xi) => { const v = spec.values[yi]?.[xi]; row['x' + xi] = typeof v === 'number' ? v : null })
        return row
      })
    },
    summary: `${title(spec)}Heatmap of ${ys.length} × ${xs.length} values.` + (peak ? ` Highest ${fmt(spec, ctx, peak.value[2])} (${peak.name}).` : ''),
    valueFormat: valueFormatOf(spec)
  }
}

const calendarSchema = s.object({
  kind: s.literal('calendar'),
  days: s.array(s.object({ date: s.string({ max: 10, pattern: DATE_RE }), value: nullableNumber }, { required: ['date', 'value'] }), { min: 1, max: 366 }),
  ...commonChartProps
}, {
  required: ['kind', 'days'],
  refine (v, _p, report) {
    const dates = (v.days as { date: string }[]).map(d => d.date).sort()
    if (new Set(dates).size !== dates.length) report('days', 'dates must be unique')
    const span = (Date.parse(dates[dates.length - 1] ?? '') - Date.parse(dates[0] ?? '')) / 86400000
    if (span > 366) report('days', 'must fit within one year')
  }
})

function compileCalendar (spec: any, ctx: ChartContext): CompiledChart {
  const days: { date: string, value: number | null }[] = [...spec.days].sort((a, b) => a.date.localeCompare(b.date))
  const lang = toBcp47(ctx.locale)
  const dayNames = Array.from({ length: 7 }, (_v, i) => new Intl.DateTimeFormat(lang, { weekday: 'narrow', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 7 + i))))
  const monthNames = Array.from({ length: 12 }, (_v, i) => new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, i, 1))))
  const range = visualRange(days.map(d => d.value))
  const option = {
    ...baseOption(ctx),
    tooltip: tooltipOption(ctx, 'item'),
    visualMap: visualMap(ctx, range),
    calendar: {
      range: [days[0]!.date, days[days.length - 1]!.date],
      top: 24,
      left: 32,
      right: 8,
      bottom: 40,
      cellSize: ['auto', 'auto'],
      splitLine: { show: false },
      itemStyle: { color: ctx.theme.surface, borderColor: withAlpha(ctx.theme.axisLine, 0.5), borderWidth: 1 },
      dayLabel: { firstDay: 1, nameMap: dayNames, color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize },
      monthLabel: { nameMap: monthNames, color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize },
      yearLabel: { show: false }
    },
    series: [{
      type: 'heatmap',
      coordinateSystem: 'calendar',
      name: spec.title ?? ctx.labels.value,
      data: days.map(d => ({ name: d.date, value: [d.date, d.value] }))
    }]
  }
  const peak = days.reduce<typeof days[number] | null>((best, d) => (typeof d.value === 'number' && (!best || d.value > (best.value as number)) ? d : best), null)
  return {
    option,
    modules: ['HeatmapChart', 'CalendarComponent', 'VisualMapComponent', 'TooltipComponent', 'AriaComponent'],
    renderer: 'svg',
    table: {
      columns: [{ key: 'date', label: ctx.labels.date }, { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' }],
      rows: days.map(d => ({ date: d.date, value: d.value }))
    },
    summary: `${title(spec)}Calendar of ${days.length} days from ${days[0]!.date} to ${days[days.length - 1]!.date}.` + (peak ? ` Highest ${fmt(spec, ctx, peak.value)} on ${peak.date}.` : ''),
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Radar

const radarSchema = s.object({
  kind: s.literal('radar'),
  indicators: s.array(s.object({ label: LABEL, max: s.number({ min: 0 }) }, { required: ['label', 'max'] }), { min: 3, max: 12 }),
  series: s.array(s.object({ name: LABEL, data: s.array(nullableNumber, { min: 3, max: 12 }), intent }, { required: ['name', 'data'] }), { min: 1, max: 8 }),
  ...commonChartProps
}, {
  required: ['kind', 'indicators', 'series'],
  refine (v, _p, report) {
    const n = (v.indicators as unknown[]).length
    ;(v.series as { data: unknown[] }[]).forEach((x, i) => {
      if (x.data.length !== n) report(`series[${i}].data`, `must have one value per indicator (${n}), got ${x.data.length}`)
    })
  }
})

function compileRadar (spec: any, ctx: ChartContext): CompiledChart {
  const indicators: { label: string, max: number }[] = spec.indicators
  const series: { name: string, data: (number | null)[], intent?: any }[] = spec.series
  const showLegend = spec.showLegend ?? series.length > 1
  const option = {
    ...baseOption(ctx),
    color: series.map((x, i) => colorFor(ctx, i, x.intent)),
    tooltip: tooltipOption(ctx, 'item'),
    ...(showLegend ? { legend: legendOption(ctx, 'bottom') } : {}),
    radar: {
      indicator: indicators.map(ind => ({ name: ind.label, max: ind.max || 1 })),
      shape: 'polygon',
      splitNumber: 4,
      radius: showLegend ? '62%' : '70%',
      center: ['50%', showLegend ? '46%' : '50%'],
      axisName: { color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize },
      splitLine: { lineStyle: { color: ctx.theme.splitLine } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: ctx.theme.splitLine } }
    },
    series: [{
      type: 'radar',
      symbol: 'circle',
      symbolSize: 5,
      emphasis: { focus: 'self' },
      data: series.map((x, i) => {
        const color = colorFor(ctx, i, x.intent)
        return {
          name: x.name,
          value: indicators.map((_ind, ii) => typeof x.data[ii] === 'number' ? x.data[ii] : '-'),
          lineStyle: { color, width: 2 },
          itemStyle: { color },
          areaStyle: { color: withAlpha(color, 0.14) }
        }
      })
    }]
  }
  const modules: EChartsModule[] = ['RadarChart', 'RadarComponent', 'TooltipComponent', 'AriaComponent']
  if (showLegend) modules.push('LegendComponent')
  return {
    option,
    modules,
    renderer: 'svg',
    table: {
      columns: [{ key: 'label', label: ctx.labels.category }, ...series.map((x, i) => ({ key: 's' + i, label: x.name, format: spec.format, align: 'end' as const }))],
      rows: indicators.map((ind, ii) => {
        const row: Record<string, string | number | null> = { label: ind.label }
        series.forEach((x, i) => { row['s' + i] = typeof x.data[ii] === 'number' ? x.data[ii] : null })
        return row
      })
    },
    summary: `${title(spec)}Radar chart comparing ${series.map(x => x.name).join(', ')} on ${indicators.length} measures: ${indicators.map(i => i.label).join(', ')}.`,
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Sankey (flows between labelled nodes; must be acyclic)

const sankeySchema = s.object({
  kind: s.literal('sankey'),
  nodes: s.array(s.object({ label: LABEL, intent }, { required: ['label'] }), { min: 2, max: 60 }),
  links: s.array(s.object({ source: LABEL, target: LABEL, value: s.number({ min: 0 }) }, { required: ['source', 'target', 'value'] }), { min: 1, max: 200 }),
  orient: s.enumOf(['horizontal', 'vertical']),
  ...commonChartProps
}, {
  required: ['kind', 'nodes', 'links'],
  refine (v, _p, report) {
    const labels = (v.nodes as { label: string }[]).map(n => n.label)
    if (new Set(labels).size !== labels.length) report('nodes', 'node labels must be unique')
    const known = new Set(labels)
    const links = v.links as { source: string, target: string }[]
    links.forEach((l, i) => {
      if (!known.has(l.source)) report(`links[${i}].source`, 'is not a node label')
      if (!known.has(l.target)) report(`links[${i}].target`, 'is not a node label')
      if (l.source === l.target) report(`links[${i}]`, 'cannot link a node to itself')
    })
    if (acyclicLinks(links).length !== links.filter(l => l.source !== l.target).length) report('links', 'must not form a cycle; sankey flows go one way')
  }
})

/** Links in order, skipping any that would close a cycle (ECharts sankey rejects cycles). */
export function acyclicLinks<T extends { source: string, target: string }> (links: T[]): T[] {
  const out: T[] = []
  const edges = new Map<string, Set<string>>()
  const reaches = (from: string, to: string): boolean => {
    const seen = new Set<string>()
    const stack = [from]
    while (stack.length) {
      const n = stack.pop()!
      if (n === to) return true
      if (seen.has(n)) continue
      seen.add(n)
      for (const next of edges.get(n) ?? []) stack.push(next)
    }
    return false
  }
  for (const link of links) {
    if (link.source === link.target || reaches(link.target, link.source)) continue
    if (!edges.has(link.source)) edges.set(link.source, new Set())
    edges.get(link.source)!.add(link.target)
    out.push(link)
  }
  return out
}

function compileSankey (spec: any, ctx: ChartContext): CompiledChart {
  const nodes: { label: string, intent?: any }[] = spec.nodes
  const known = new Set(nodes.map(n => n.label))
  const links = acyclicLinks((spec.links as { source: string, target: string, value: number }[]).filter(l => known.has(l.source) && known.has(l.target)))
  const vertical = spec.orient === 'vertical'
  const option = {
    ...baseOption(ctx),
    tooltip: tooltipOption(ctx, 'item'),
    series: [{
      type: 'sankey',
      name: spec.title ?? ctx.labels.value,
      orient: vertical ? 'vertical' : 'horizontal',
      left: 8,
      right: vertical ? 8 : 96,
      top: 8,
      bottom: vertical ? 48 : 8,
      nodeWidth: 14,
      nodeGap: 10,
      nodeAlign: 'justify',
      layoutIterations: 32,
      draggable: false,
      emphasis: { focus: 'adjacency' },
      label: { color: ctx.theme.text, fontSize: ctx.theme.fontSize + 1, ...(vertical ? { position: 'bottom' } : {}) },
      lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.32 },
      itemStyle: { borderWidth: 0, borderRadius: 2 },
      data: nodes.map((n, i) => ({ name: n.label, itemStyle: { color: colorFor(ctx, i, n.intent) } })),
      links: links.map(l => ({ source: l.source, target: l.target, value: l.value }))
    }]
  }
  const total = sum(links.filter(l => !links.some(o => o.target === l.source)).map(l => l.value))
  return {
    option,
    modules: ['SankeyChart', 'TooltipComponent', 'AriaComponent'],
    renderer: 'svg',
    table: {
      columns: [{ key: 'source', label: ctx.labels.from }, { key: 'target', label: ctx.labels.to }, { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' }],
      rows: links.map(l => ({ source: l.source, target: l.target, value: l.value }))
    },
    summary: `${title(spec)}Flow diagram of ${fmt(spec, ctx, total)} through ${nodes.length} steps and ${links.length} links.`,
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Candlestick

const candlestickSchema = s.object({
  kind: s.literal('candlestick'),
  categories: s.array(s.string({ max: 40 }), { min: 1, max: 500 }),
  values: s.array(s.array(s.number(), { min: 4, max: 4 }), { min: 1, max: 500, description: '[open, close, low, high] per category' }),
  xLabel: s.string({ max: 60 }),
  yLabel: s.string({ max: 60 }),
  zoom: s.boolean('Adds a range slider for long series'),
  ...commonChartProps
}, {
  required: ['kind', 'categories', 'values'],
  refine (v, _p, report) {
    const values = v.values as number[][]
    if (values.length !== (v.categories as unknown[]).length) report('values', 'must have one entry per category')
    values.forEach(([open, close, low, high], i) => {
      if (low! > Math.min(open!, close!) || high! < Math.max(open!, close!)) report(`values[${i}]`, 'low and high must enclose open and close')
    })
  }
})

function compileCandlestick (spec: any, ctx: ChartContext): CompiledChart {
  const categories: string[] = spec.categories
  const values: number[][] = spec.values.slice(0, categories.length)
  const up = ctx.theme.intents.positive
  const down = ctx.theme.intents.negative
  const option = {
    ...baseOption(ctx),
    tooltip: tooltipOption(ctx, 'axis', 'line'),
    grid: { left: 8, right: 16, top: 16, bottom: spec.zoom ? 44 : 8, containLabel: true },
    xAxis: { ...categoryAxis(ctx, categories, spec.xLabel), boundaryGap: true },
    yAxis: { ...valueAxis(ctx, spec.yLabel), scale: true },
    series: [{
      type: 'candlestick',
      name: spec.title ?? ctx.labels.value,
      barMaxWidth: 16,
      itemStyle: { color: up, color0: down, borderColor: up, borderColor0: down },
      data: values
    }],
    ...(spec.zoom ? { dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8, borderColor: 'transparent', fillerColor: withAlpha(ctx.theme.palette[0]!, 0.12) }] } : {})
  }
  const modules: EChartsModule[] = ['CandlestickChart', 'GridComponent', 'TooltipComponent', 'AriaComponent']
  if (spec.zoom) modules.push('DataZoomComponent')
  const closes = values.map(v => v[1] ?? null)
  return {
    option,
    modules,
    renderer: values.length > 300 ? 'canvas' : 'svg',
    table: {
      columns: [
        { key: 'category', label: spec.xLabel ?? ctx.labels.category },
        { key: 'open', label: ctx.labels.open, format: spec.format, align: 'end' },
        { key: 'close', label: ctx.labels.close, format: spec.format, align: 'end' },
        { key: 'low', label: ctx.labels.low, format: spec.format, align: 'end' },
        { key: 'high', label: ctx.labels.high, format: spec.format, align: 'end' }
      ],
      rows: values.map((v, i) => ({ category: categories[i]!, open: v[0]!, close: v[1]!, low: v[2]!, high: v[3]! }))
    },
    summary: `${title(spec)}Range chart over ${values.length} ${spec.xLabel ?? ctx.labels.category.toLowerCase()} values.` + describeExtremes(categories, closes, ctx, spec),
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Graph (network of nodes and edges)

const graphSchema = s.object({
  kind: s.literal('graph'),
  nodes: s.array(s.object({
    id: s.string({ max: 64, pattern: NODE_ID_RE }),
    label: LABEL,
    value: s.number({ min: 0, description: 'Scales the node size' }),
    group: LABEL,
    intent
  }, { required: ['id', 'label'] }), { min: 2, max: 100 }),
  edges: s.array(s.object({
    source: s.string({ max: 64, pattern: NODE_ID_RE }),
    target: s.string({ max: 64, pattern: NODE_ID_RE }),
    value: s.number({ min: 0 }),
    label: s.string({ max: 60 })
  }, { required: ['source', 'target'] }), { max: 300 }),
  layout: s.enumOf(['force', 'circular']),
  ...commonChartProps
}, {
  required: ['kind', 'nodes', 'edges'],
  refine (v, _p, report) {
    const ids = (v.nodes as { id: string }[]).map(n => n.id)
    if (new Set(ids).size !== ids.length) report('nodes', 'node ids must be unique')
    const known = new Set(ids)
    ;(v.edges as { source: string, target: string }[]).forEach((e, i) => {
      if (!known.has(e.source)) report(`edges[${i}].source`, 'is not a node id')
      if (!known.has(e.target)) report(`edges[${i}].target`, 'is not a node id')
    })
  }
})

function compileGraph (spec: any, ctx: ChartContext): CompiledChart {
  const nodes: { id: string, label: string, value?: number, group?: string, intent?: any }[] = spec.nodes
  const known = new Map(nodes.map(n => [n.id, n]))
  const edges = (spec.edges as { source: string, target: string, value?: number, label?: string }[]).filter(e => known.has(e.source) && known.has(e.target))
  const groups = [...new Set(nodes.map(n => n.group).filter((g): g is string => !!g))]
  const values = nodes.map(n => n.value).filter((v): v is number => typeof v === 'number')
  const maxValue = values.length ? Math.max(...values) : 0
  const circular = spec.layout === 'circular'
  const showLegend = spec.showLegend ?? groups.length > 1
  const option = {
    ...baseOption(ctx),
    color: groups.length ? groups.map((_g, i) => colorFor(ctx, i)) : [ctx.theme.palette[0]],
    tooltip: tooltipOption(ctx, 'item'),
    ...(showLegend ? { legend: { ...legendOption(ctx, 'bottom'), data: groups } } : {}),
    series: [{
      type: 'graph',
      name: spec.title ?? ctx.labels.value,
      layout: circular ? 'circular' : 'force',
      ...(circular ? { circular: { rotateLabel: true } } : { force: { repulsion: 160, edgeLength: [40, 120], gravity: 0.08, layoutAnimation: !reducedMotion(ctx) } }),
      roam: false,
      draggable: false,
      top: 16,
      bottom: showLegend ? 40 : 16,
      left: 16,
      right: 16,
      categories: groups.map(name => ({ name })),
      label: { show: nodes.length <= 40, position: 'right', color: ctx.theme.text, fontSize: ctx.theme.fontSize },
      edgeLabel: { show: edges.some(e => e.label) && edges.length <= 30, color: ctx.theme.textMuted, fontSize: ctx.theme.fontSize - 1, formatter: '{c}' },
      lineStyle: { color: ctx.theme.axisLine, width: 1.2, opacity: 0.8, curveness: circular ? 0.3 : 0.08 },
      emphasis: { focus: 'adjacency', lineStyle: { width: 2 } },
      data: nodes.map(n => ({
        id: n.id,
        name: n.label,
        value: n.value,
        symbolSize: maxValue && typeof n.value === 'number' ? 10 + 30 * Math.sqrt(n.value / maxValue) : 14,
        ...(n.group ? { category: groups.indexOf(n.group) } : {}),
        ...(n.intent && n.intent !== 'neutral' ? { itemStyle: { color: colorFor(ctx, 0, n.intent) } } : {})
      })),
      links: edges.map(e => ({ source: e.source, target: e.target, ...(typeof e.value === 'number' ? { value: e.value } : {}), ...(e.label ? { value: e.label } : {}) }))
    }]
  }
  const modules: EChartsModule[] = ['GraphChart', 'TooltipComponent', 'AriaComponent']
  if (showLegend) modules.push('LegendComponent')
  const degree = new Map<string, number>()
  edges.forEach(e => { degree.set(e.source, (degree.get(e.source) ?? 0) + 1); degree.set(e.target, (degree.get(e.target) ?? 0) + 1) })
  const hub = [...degree.entries()].sort((a, b) => b[1] - a[1])[0]
  return {
    option,
    modules,
    renderer: 'svg',
    table: {
      columns: [{ key: 'source', label: ctx.labels.from }, { key: 'target', label: ctx.labels.to }, { key: 'value', label: ctx.labels.value, format: spec.format, align: 'end' }],
      rows: edges.map(e => ({ source: known.get(e.source)!.label, target: known.get(e.target)!.label, value: typeof e.value === 'number' ? e.value : (e.label ?? null) }))
    },
    summary: `${title(spec)}Network of ${nodes.length} nodes and ${edges.length} connections.` + (hub ? ` Most connected: ${known.get(hub[0])!.label} (${hub[1]}).` : ''),
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------
// Scatter

const scatterSchema = s.object({
  kind: s.literal('scatter'),
  series: s.array(s.object({
    name: LABEL,
    points: s.array(s.array(s.number(), { min: 2, max: 2 }), { min: 1, max: 1000, description: '[x, y] pairs' }),
    intent
  }, { required: ['name', 'points'] }), { min: 1, max: 8 }),
  xLabel: s.string({ max: 60 }),
  yLabel: s.string({ max: 60 }),
  ...commonChartProps
}, { required: ['kind', 'series'] })

function compileScatter (spec: any, ctx: ChartContext): CompiledChart {
  const series: { name: string, points: number[][], intent?: any }[] = spec.series
  const showLegend = spec.showLegend ?? series.length > 1
  const count = sum(series.map(x => x.points.length))
  const option = {
    ...baseOption(ctx),
    color: series.map((x, i) => colorFor(ctx, i, x.intent)),
    tooltip: tooltipOption(ctx, 'item'),
    grid: { left: 8, right: 16, top: showLegend ? 40 : 16, bottom: 8, containLabel: true },
    xAxis: { ...valueAxis(ctx, spec.xLabel), scale: true, nameLocation: 'middle', nameGap: 28 },
    yAxis: { ...valueAxis(ctx, spec.yLabel), scale: true },
    ...(showLegend ? { legend: legendOption(ctx) } : {}),
    series: series.map((x, i) => ({
      type: 'scatter',
      name: x.name,
      symbolSize: count > 400 ? 5 : 9,
      itemStyle: { color: colorFor(ctx, i, x.intent), opacity: 0.78 },
      emphasis: { focus: 'series' },
      data: x.points
    }))
  }
  const modules: EChartsModule[] = ['ScatterChart', 'GridComponent', 'TooltipComponent', 'AriaComponent']
  if (showLegend) modules.push('LegendComponent')
  return {
    option,
    modules,
    renderer: count > 2000 ? 'canvas' : 'svg',
    table: {
      columns: [
        { key: 'series', label: ctx.labels.series },
        { key: 'x', label: spec.xLabel ?? 'x', align: 'end' },
        { key: 'y', label: spec.yLabel ?? 'y', format: spec.format, align: 'end' }
      ],
      rows: series.flatMap(x => x.points.map(p => ({ series: x.name, x: p[0]!, y: p[1]! })))
    },
    summary: `${title(spec)}Scatter plot of ${count} points in ${series.length} series.`,
    valueFormat: valueFormatOf(spec)
  }
}

// ---------------------------------------------------------------------------

registerChartKind('funnel', { schema: funnelSchema, compile: compileFunnel })
registerChartKind('gauge', { schema: gaugeSchema, compile: compileGauge })
registerChartKind('treemap', { schema: hierarchySchema('treemap'), compile: compileTreemap })
registerChartKind('sunburst', { schema: hierarchySchema('sunburst'), compile: compileSunburst })
registerChartKind('heatmap', { schema: heatmapSchema, compile: compileHeatmap })
registerChartKind('calendar', { schema: calendarSchema, compile: compileCalendar })
registerChartKind('radar', { schema: radarSchema, compile: compileRadar })
registerChartKind('sankey', { schema: sankeySchema, compile: compileSankey })
registerChartKind('candlestick', { schema: candlestickSchema, compile: compileCandlestick })
registerChartKind('graph', { schema: graphSchema, compile: compileGraph })
registerChartKind('scatter', { schema: scatterSchema, compile: compileScatter })

export const ADVANCED_CHART_KINDS = ['funnel', 'gauge', 'treemap', 'sunburst', 'heatmap', 'calendar', 'radar', 'sankey', 'candlestick', 'graph', 'scatter'] as const
